import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  MaterializedProcessesDataTableQuery,
  ProjectionKind,
} from 'generated/resources/datatable.graphql'
import { ProcessesProcessCategoryEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { Statuses } from 'modules/processes/domain'

import { ContractDate } from './ContractDate'
import { ItemProgress } from './ItemProgress'
import { MaterializedProcessName } from './MaterializedProcessName'
import { NextTaskDueDate } from './NextTaskDueDate'
import { RowActions } from './RowActions'

export type ProcessDataTable =
  MaterializedProcessesDataTableQuery['processes']['materializedProcessesConnection']['edges'][number]['node']

export const projections = ({
  processCategory,
  isBoarding,
}: {
  processCategory?: ProcessesProcessCategoryEnum | null
  isBoarding?: boolean
}) => {
  const projections = [
    {
      colSpan: 2,
      template: '{{ name }}',
      identifier: 'name',
      kind: ProjectionKind.Custom,
      title: i18n.t('processes.process.employee.table.columns.name'),
      render: ({ name, status, resourceRelationships }: ProcessDataTable) =>
        MaterializedProcessName(name!, status!, resourceRelationships),
    },

    {
      colSpan: 2,
      template: '{{ completedResources }}',
      identifier: 'task-completed',
      kind: ProjectionKind.Custom,
      justifyContent: 'center',
      textAlign: 'center',
      title: i18n.t('processes.process.employee.table.columns.tasks'),
      render: (materializedProcess: ProcessDataTable) => {
        const completedResources = materializedProcess.completedResources ?? 0
        const totalResources = materializedProcess.totalResources ?? 0

        return (
          <ItemProgress
            completedItems={completedResources}
            totalItems={totalResources}
            status={materializedProcess.status as unknown as Statuses}
          />
        )
      },
    },
    {
      colSpan: 2,
      template: '{{ nextItemDueDate }}',
      identifier: 'overdue',
      kind: ProjectionKind.Custom,
      justifyContent: 'center',
      textAlign: 'center',
      title: i18n.t('processes.process.employee.table.columns.next_task_due_date'),
      render: (materializedProcess: ProcessDataTable) => (
        <NextTaskDueDate
          nextDueDate={materializedProcess.nextItemDueDate}
          status={materializedProcess.status}
        />
      ),
    },
    {
      identifier: 'actions',
      kind: ProjectionKind.Decorative,
      render: ({ id, process, status }: ProcessDataTable) => (
        <RowActions status={status!} processId={process.id!} id={id!} />
      ),
      colSpan: 1,
    },
  ]

  const isOnboarding = processCategory === 'onboarding'
  const isOffboarding = processCategory === 'offboarding'
  const isTraining = processCategory === 'training'

  if (isBoarding) {
    const title = isOnboarding
      ? i18n.t('processes.process.employee.table.columns.contract_starts_on')
      : isOffboarding
      ? i18n.t('processes.process.employee.table.columns.contract_ends_on')
      : ''

    const template = isOnboarding
      ? '{{ contractStartsOn }}'
      : isOffboarding
      ? '{{ contractEndsOn }}'
      : ''

    projections.splice(1, 0, {
      colSpan: 2,
      template: template,
      identifier: 'contract-date',
      kind: ProjectionKind.Custom,
      justifyContent: 'center',
      textAlign: 'center',
      title: title,
      render: (materializedProcess: ProcessDataTable) => {
        return (
          <ContractDate
            contractEndsOn={materializedProcess.contractEndsOn}
            contractStartsOn={materializedProcess.contractStartsOn}
            status={materializedProcess.status}
            processCategory={processCategory as ProcessesProcessCategoryEnum}
          />
        )
      },
    })
  }

  if (isTraining) {
    projections.splice(1, 0, {
      colSpan: 2,
      template: '{{ trainingName }}',
      identifier: 'training-name',
      kind: ProjectionKind.Custom,
      justifyContent: 'left',
      textAlign: 'center',
      title: i18n.t('processes.process.employee.table.columns.training_name'),
      render: (materializedProcess: ProcessDataTable) => {
        return (
          <Box>
            <Text.Normal>{materializedProcess.trainingName}</Text.Normal>
          </Box>
        )
      },
    })
  }

  return projections
}
